import { useState, useCallback, useRef } from 'react';
import { PipelineStep, PipelineStepStatus, UPLOAD_PIPELINE_STEPS } from '../components/Upload/UploadPipelineProgress';

/**
 * Hook to manage upload pipeline step progression.
 *
 * Frontend steps (compress, upload) are advanced manually by the caller.
 * Backend steps (moderation → resize → ai_analysis → database → indexing)
 * are animated on a timer once the API call starts, then resolved when
 * the API returns success or error.
 *
 * Typical backend timing:
 *   moderation: ~2s, resize: ~2s, ai_analysis: ~8s, database: ~1s, indexing: ~1s
 */

const BACKEND_STEP_IDS = ['moderation', 'resize', 'ai_analysis', 'database', 'indexing'];
// Estimated duration per backend step in ms (used for animation pacing)
const BACKEND_STEP_DURATIONS: Record<string, number> = {
  moderation: 2000,
  resize: 2000,
  ai_analysis: 8000,
  database: 1500,
  indexing: 1500,
};

export interface UseUploadPipelineReturn {
  steps: PipelineStep[];
  /** Advance a specific step to a status */
  setStepStatus: (stepId: string, status: PipelineStepStatus) => void;
  /** Start animating backend steps (call when API request begins) */
  startBackendAnimation: () => void;
  /** Resolve all remaining steps as done (call on API success) */
  resolveAll: () => void;
  /** Mark the current active backend step as error, stop animation */
  failAtCurrent: (failedStepId?: string) => void;
  /** Reset all steps to pending */
  reset: () => void;
}

export function useUploadPipeline(): UseUploadPipelineReturn {
  const [steps, setSteps] = useState<PipelineStep[]>(
    UPLOAD_PIPELINE_STEPS.map(s => ({ ...s, status: 'pending' as PipelineStepStatus }))
  );

  const animationTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const resolvedRef = useRef(false);

  const clearAnimationTimers = useCallback(() => {
    animationTimers.current.forEach(t => clearTimeout(t));
    animationTimers.current = [];
  }, []);

  const setStepStatus = useCallback((stepId: string, status: PipelineStepStatus) => {
    setSteps(prev => prev.map(s => s.id === stepId ? { ...s, status } : s));
  }, []);

  /**
   * Animate through backend steps on a timer.
   * Each step becomes 'active' for its estimated duration, then 'done'.
   * If the API resolves before animation finishes, resolveAll() snaps
   * all remaining steps to 'done'.
   */
  const startBackendAnimation = useCallback(() => {
    resolvedRef.current = false;
    clearAnimationTimers();

    let cumulativeDelay = 0;

    BACKEND_STEP_IDS.forEach((stepId, idx) => {
      const duration = BACKEND_STEP_DURATIONS[stepId] || 2000;

      // Set step to 'active' after cumulative delay
      const activateTimer = setTimeout(() => {
        if (resolvedRef.current) return;
        setSteps(prev => prev.map(s => s.id === stepId ? { ...s, status: 'active' } : s));
      }, cumulativeDelay);
      animationTimers.current.push(activateTimer);

      // Set step to 'done' after its duration (unless it's the last step)
      if (idx < BACKEND_STEP_IDS.length - 1) {
        const doneTimer = setTimeout(() => {
          if (resolvedRef.current) return;
          setSteps(prev => prev.map(s => s.id === stepId ? { ...s, status: 'done' } : s));
        }, cumulativeDelay + duration);
        animationTimers.current.push(doneTimer);
      }

      cumulativeDelay += duration;
    });
  }, [clearAnimationTimers]);

  /**
   * Snap all steps to 'done'. Called when the API returns success.
   */
  const resolveAll = useCallback(() => {
    resolvedRef.current = true;
    clearAnimationTimers();
    setSteps(prev => prev.map(s => ({ ...s, status: 'done' as PipelineStepStatus })));
  }, [clearAnimationTimers]);

  /**
   * Mark the current active (or specified) step as error and stop.
   * Steps after the failed one stay 'pending'.
   */
  const failAtCurrent = useCallback((failedStepId?: string) => {
    resolvedRef.current = true;
    clearAnimationTimers();
    setSteps(prev => {
      const failId = failedStepId || prev.find(s => s.status === 'active')?.id;
      if (!failId) return prev;
      let pastFail = false;
      return prev.map(s => {
        if (s.id === failId) {
          pastFail = true;
          return { ...s, status: 'error' as PipelineStepStatus };
        }
        if (pastFail) return { ...s, status: 'pending' as PipelineStepStatus };
        return s;
      });
    });
  }, [clearAnimationTimers]);

  const reset = useCallback(() => {
    resolvedRef.current = false;
    clearAnimationTimers();
    setSteps(UPLOAD_PIPELINE_STEPS.map(s => ({ ...s, status: 'pending' as PipelineStepStatus })));
  }, [clearAnimationTimers]);

  return { steps, setStepStatus, startBackendAnimation, resolveAll, failAtCurrent, reset };
}
